import { Component, OnDestroy, OnInit } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { Router } from '@angular/router';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ApplicationService } from 'services/application/application.service';
import { AuthService } from 'services/authentication/auth.service';
import { AwardProgramInfo, AwardsService } from 'services/awards/awards.service';
import { DialogService, OptionBoxDialogOptions } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { SplashService } from 'services/splash/splash.service';
import { BaseInputBoxDialogOptions } from '../../services/dialogs/inputbox/inputbox.dialogcomponent';
import { SettingsService } from '../../services/settings/settings.service';

@Component({
    selector: 'app-login',
    styleUrls: ['login.component.css'],
    templateUrl: 'login.component.html'
})

export class LoginComponent implements OnDestroy, OnInit {
    private _failures: number;
    private _subrelease: Subject<void> = new Subject<void>();

    AwardInfo: AwardProgramInfo;
    LastSizeCode: string;
    LoginInProgress: boolean;
    ShowImpersonationBox: boolean;
    ShowingParent: boolean;
    ShowingUpdate: boolean;
    SmallDisplay: boolean;
    ViewReady: boolean;
    IsOnline: boolean;
    username: string;
    password: string;
	judgingKey: string;
	SchoolYear: string;

    constructor(private authService: AuthService,
        private awardsService: AwardsService, private errorService: ErrorService,
        private mediaChange: MediaObserver, private splashService: SplashService,
		private dialogService: DialogService, private router: Router,
		private appService: ApplicationService, private settingsService: SettingsService) {
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;
        this._failures = 0; // No login failures to start.
        this.LoginInProgress = false;
        this.ShowImpersonationBox = false;
        this.ShowingParent = true;
        this.ShowingUpdate = false;
        self.ViewReady = false;
        self.IsOnline = true;

        self.LastSizeCode = (self.mediaChange.isActive('xs') ? 'xs' :
            (self.mediaChange.isActive('sm') ? 'sm' :
            (self.mediaChange.isActive('md') ? 'md' :
            (self.mediaChange.isActive('lg') ? 'lg' : 'xl'))));
        self.SmallDisplay = (self.LastSizeCode === 'xs' || self.LastSizeCode === 'sm');

        self.mediaChange.media$
            .pipe(
                takeUntil(self._subrelease)
            ).subscribe(media => {
                self.SmallDisplay = (media.mqAlias === 'xs' || media.mqAlias === 'sm');
                self.LastSizeCode = media.mqAlias;
            }
        );

        // Set up a subscription for which awards program is currently being used.
        self.AwardInfo = self.awardsService.CurrentAwards;
		self.awardsService.CurrentAwardsSub.pipe(takeUntil(self._subrelease)).subscribe(award => {
			if (award == null) return;
			self.AwardInfo = award;

			self.settingsService.CurrentSchoolYearSubject.pipe(takeUntil(this._subrelease)).subscribe(year => {
				self.SchoolYear = year;
				this.ViewReady = true;
			});
        });

        //Test if app in online
        self.appService.IsAppOnline().pipe(takeUntil(this._subrelease)).subscribe(IsOnline => {
            self.IsOnline = IsOnline;
        },
            error => { self.IsOnline = false });
    }

    judgeLogin(form: any) {
        if (!form.judgingKey) {
            this.splashService.SnackbarError('No Judging Key Provided', 5000);

            document.getElementById('judgingKey').focus();
            return;
        }

		if (this.LoginInProgress === true) { return; }

		let url = this.router.url;
		let awardName = url.split('/')[1];

        this.LoginInProgress = true;
        this.splashService.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Logging in...' });

		this.authService.ValidateJudge(form.judgingKey, this.SchoolYear, awardName).pipe(takeUntil(this._subrelease)).subscribe(info => {
            // Drop the splash that's on the screen.
            this.splashService.CloseSplash();
            this.LoginInProgress = false;

            // Use the user service to process this login, which will handle routing to the proper home page for us.
            this.authService.ProcessLogin(info);

        }, error => {
            this.splashService.CloseSplash();
            this.LoginInProgress = false;

            // Fetch the error packet
            const errObj = this.errorService.RetrieveServiceError(error);
            this.splashService.SnackbarError(errObj.ErrorMessage, 8000);
        });
    }

    LostKey() {
        let options: BaseInputBoxDialogOptions = new BaseInputBoxDialogOptions();
        options.TitleText = 'Key Recovery';
        options.MessageText = 'Please enter the email address that is attached to this site and we will resend the introductory email to that address. The email contains the key to login.';

        this.dialogService.OpenInputBoxDialog(options).pipe(takeUntil(this._subrelease)).subscribe(res => {
			if (res != null) {
				this.appService.EmailJudgingKey(res, this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe(result => {
                    this.splashService.SnackbarSuccess('Email Sent', 5000);
                }, error => this.errorService.HandleServiceError(error));
            }
        });
    }

    ShowImpersonation(event: any) {
        if (!event) { return; }

        if (event.ctrlKey || event.metaKey || event.tapCount === 2) {
            this.ShowImpersonationBox = !this.ShowImpersonationBox;
        }
    }

    staffLogin(form: any) {
        const self = this;

		let url = self.router.url;
		let awardName = url.split('/')[1].toUpperCase();

        // Check to see if we have everything needed. We need a username and a password, and the password cannot have an @
        // symbol in it.
        if (!form.username) {
            self.splashService.SnackbarError('Please provide a username.', 5000);

            document.getElementById('username').focus();
            return;
        }

        if (!form.password) {
            self.splashService.SnackbarError('Please provide a password.', 5000);

            document.getElementById('password').focus();
            return;
        }

        // Check the username for an '@' symbol. If found, don't allow.
        if (form.username && form.username.indexOf('@') >= 0) {
            self.dialogService.OpenMessageBoxDialog({
                TitleText: 'Username Issue Detected',
                MessageText: `You appear to be attempting to use an email address as your username. If you are a nominee or judge who ` +
                `is not a county employee, your username is not your email address. Please use your assigned username to ` +
                `log in to this system.<br /><br /><strong>District Staff:</strong> You do not need to provide the "@polk-fl.net" ` +
                `portion of your email address for your username.`,
                OKText: 'OK' })
                .pipe(takeUntil(self._subrelease)).subscribe();

            document.getElementById('username').focus();
            return;
        }

        if (self.LoginInProgress === true) { return; }

        self.LoginInProgress = true;
        self.splashService.OpenSplash({TitleText: 'Please Wait', MessageText: 'Logging in...'});

        // Grab the screen and window resolutions
        const scr = window.screen.width + 'x' + window.screen.height;
        const win = window.innerWidth + 'x' + window.innerHeight;

		self.authService.ValidateUser(form.username, form.password, awardName, self.SchoolYear, form.impersonated, form.training, scr, win)
            .pipe(takeUntil(self._subrelease)).subscribe(info => {
                // Drop the splash that's on the screen.
                self.splashService.CloseSplash();
                self.LoginInProgress = false;

				self.appService.GetNominationsByWorkLocation(info.AuthToken, info.SchoolNumber, info.AwardID, self.SchoolYear).pipe(takeUntil(self._subrelease)).subscribe(res => {
                    // Use the user service to process this login, which will handle routing to the proper home page for us.
                    this.authService.ProcessLogin(info);
                });

            }, error => {
                this.splashService.CloseSplash();
                this.LoginInProgress = false;

                // Fetch the error packet
                const errObj = self.errorService.RetrieveServiceError(error);

                if (errObj.ErrorObject.status === 401) {
                    // Unauthorized. Typically a failed username/password.
                    self.splashService.SnackbarError(errObj.ErrorMessage, 8000);

                    self._failures++;

                    if (self._failures === 3) {
                        // Time to pop up a choice box because they are still having problems.
                        const options: OptionBoxDialogOptions = {
                            TitleText: 'Problems Logging In?',
                            MessageText: `You appear to be having some difficulty logging in to the system. Do you require any assistance?`,
                            Options: [{
                                DisplayText: 'I forgot my password.',
                                Color: 'warn',
                                Response: 'password'
                            }, {
                                DisplayText: `No - I'll try again`,
                                Color: 'primary',
                                Response: 'cancel'
                            }]
                        };

                        self.dialogService.OpenOptionBoxDialog(options)
                            .pipe(takeUntil(self._subrelease)).subscribe(response => {
                                // Reset the failures back to 0.
                                self._failures = 0;

                                switch (response) {
                                    case 'username': // Username forgotten. Collect email address and send on.
                                    // this.ForgotUsername();

                                        break;
                                    case 'password': // Password forgotten. Collect email address and send on.
                                        self.dialogService.OpenMessageBoxDialog({
                                            TitleText: 'Getting Password Help',
                                            MessageText: `To change your password, you can click on the "Need Password Help?" link ` +
                                            `on the login form to go to the Password Reset Tool. This tool can assist you in resetting ` +
                                            `your password.<br /><br /><strong>Note: </strong>This tool is only for the use of district ` +
                                            `staff, nominees who are not county staff, and judges who are not county staff. `,
                                            OKText: 'OK'
                                        });

                                        break;
                                    case 'cancel': // User wishes to cancel and try again.
                                        break;
                                }
                        });
                    }
                } else {
                    self.splashService.SnackbarError(errObj.ErrorMessage, 8000);
                }
            });
    }

}
